import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/ui/Button";
import type { IrrigationPlanStepFormData, TankEntry } from "@/pages/main/IrrigationPlanWizardPage";
import { projectTanksByProjectIdAtom, sectorCropsBySectorIdAtom } from "@/store";
import type { StepEditDialogProps } from "../../../types";
import { computeFertigationDuration, validateFertigationTiming, validateTankRecipe } from "../../../utils/validation";

function StepEditDialog({
  isOpen,
  onClose,
  step,
  fertigationEnabled,
  projectId,
  projectData,
  onSave,
  validatePlanDuration,
  mirrorTargetSectors,
  existingMirrorSectorIds,
  mirroredTargetInfo,
  sourceSectorNames: _sourceSectorNames,
}: StepEditDialogProps) {
  // Keep sourceSectorNames in props to preserve existing dialog contract.
  void _sourceSectorNames;

  const getProjectTanks = useAtomValue(projectTanksByProjectIdAtom);
  const getSectorCrops = useAtomValue(sectorCropsBySectorIdAtom);
  const projectTanks = getProjectTanks(projectId || "");
  const activeProjectTanks = useMemo(
    () => (projectTanks || []).filter((pt: { end_date: string | null }) => !pt.end_date),
    [projectTanks]
  );

  const [formData, setFormData] = useState<{
    durationMinutes: string;
    fertigationStartDelayMinutes: string;
    fertigationDurationMinutes: string;
  }>({
    durationMinutes: "15",
    fertigationStartDelayMinutes: "0",
    fertigationDurationMinutes: "5",
  });

  const [selectedMirrorIds, setSelectedMirrorIds] = useState<string[]>([]);
  const [tankEntries, setTankEntries] = useState<TankEntry[]>([]);

  const [validationError, setValidationError] = useState<string | null>(null);
  const [fertigationDelayError, setFertigationDelayError] = useState<string | null>(null);
  const [planOverlapError, setPlanOverlapError] = useState<string | null>(null);

  const pipeWashTimeSeconds = projectData?.pipe_wash_time_seconds || 0;

  // Active sector crop for plant population
  const sectorCrops = useMemo(() => (step ? getSectorCrops(step.sectorId) : []), [step, getSectorCrops]);
  const activeSectorCrop = useMemo(() => sectorCrops.find((sc) => !sc.end_date) ?? null, [sectorCrops]);

  // Max available time for any single tank (step duration - delay - pipe wash)
  const maxTankTime = useMemo(() => {
    const duration = parseInt(formData.durationMinutes);
    const delay = parseInt(formData.fertigationStartDelayMinutes);
    const washMinutes = Math.ceil(pipeWashTimeSeconds / 60);
    if (isNaN(duration) || isNaN(delay)) return 1;
    return Math.max(1, duration - delay - washMinutes);
  }, [formData.durationMinutes, formData.fertigationStartDelayMinutes, pipeWashTimeSeconds]);

  const hasTanks = tankEntries.length > 0;
  const derivedDuration = computeFertigationDuration(tankEntries);
  const tankRecipeValidation = useMemo(() => validateTankRecipe(tankEntries), [tankEntries]);

  // Update form data when step changes
  useEffect(() => {
    if (step) {
      setFormData({
        durationMinutes: step.durationMinutes.toString(),
        fertigationStartDelayMinutes: (step.fertigationStartDelayMinutes ?? (fertigationEnabled ? 2 : 0)).toString(),
        fertigationDurationMinutes: (step.fertigationDurationMinutes || 5).toString(),
      });
      // Only load tank entries when fertigation is enabled at the plan level
      setTankEntries(fertigationEnabled ? step.tankEntries || [] : []);
    }
  }, [step, fertigationEnabled]);

  useEffect(() => {
    if (existingMirrorSectorIds) {
      setSelectedMirrorIds(existingMirrorSectorIds);
    }
  }, [existingMirrorSectorIds]);

  // Validate fertigation timing
  useEffect(() => {
    if (!step || !fertigationEnabled) {
      setValidationError(null);
      setFertigationDelayError(null);
      return;
    }

    const durationMinutes = parseInt(formData.durationMinutes);
    const fertigationStartDelayMinutes = parseInt(formData.fertigationStartDelayMinutes);
    const fertigationDurationMinutes = hasTanks ? derivedDuration : parseInt(formData.fertigationDurationMinutes);

    if (!isNaN(fertigationStartDelayMinutes) && fertigationStartDelayMinutes < 2) {
      setFertigationDelayError("O atraso da fertirrigação deve ser de no mínimo 2 minutos.");
    } else {
      setFertigationDelayError(null);
    }

    if (
      !isNaN(durationMinutes) &&
      !isNaN(fertigationStartDelayMinutes) &&
      !isNaN(fertigationDurationMinutes) &&
      durationMinutes > 0
    ) {
      const tempStep: IrrigationPlanStepFormData = {
        ...step,
        durationMinutes,
        fertigationStartDelayMinutes,
        fertigationDurationMinutes,
        tankEntries,
      };

      const validation = validateFertigationTiming(tempStep, pipeWashTimeSeconds);
      setValidationError(validation.isValid ? null : validation.errorMessage || null);
    } else {
      setValidationError(null);
    }
  }, [formData, step, fertigationEnabled, pipeWashTimeSeconds, hasTanks, derivedDuration, tankEntries]);

  useEffect(() => {
    if (!step || !validatePlanDuration) {
      setPlanOverlapError(null);
      return;
    }

    const durationMinutes = parseInt(formData.durationMinutes);
    if (isNaN(durationMinutes) || durationMinutes < 1) {
      setPlanOverlapError(null);
      return;
    }

    const updatedStep: IrrigationPlanStepFormData = {
      ...step,
      durationMinutes,
    };

    const overlapMessage = validatePlanDuration(updatedStep);
    setPlanOverlapError(overlapMessage);
  }, [formData.durationMinutes, step, validatePlanDuration]);

  // Tanks not already selected
  const availableTanks = useMemo(
    () => activeProjectTanks.filter((pt) => !tankEntries.some((t) => t.projectTankId === pt.id)),
    [activeProjectTanks, tankEntries]
  );

  const handleAddTank = () => {
    if (availableTanks.length === 0) return;
    const pt = availableTanks[0];
    setTankEntries((prev) => [
      ...prev,
      {
        projectTankId: pt.id,
        tankLabel: pt.tank.label,
        nutrientName: pt.tank.nutrient.name,
        timeMinutes: 1,
      },
    ]);
  };

  const handleRemoveTank = (projectTankId: string) => {
    setTankEntries((prev) => prev.filter((t) => t.projectTankId !== projectTankId));
  };

  const handleTankChange = (projectTankId: string, field: keyof TankEntry, value: string | number) => {
    setTankEntries((prev) =>
      prev.map((t) =>
        t.projectTankId === projectTankId
          ? { ...t, [field]: field === "projectTankId" ? value : field === "timeMinutes" ? Number(value) : value }
          : t
      )
    );
  };

  const handleSave = async () => {
    if (!step) return;

    const durationMinutes = parseInt(formData.durationMinutes);
    const fertigationStartDelayMinutes = parseInt(formData.fertigationStartDelayMinutes);
    const fertigationDurationMinutes = hasTanks ? derivedDuration : parseInt(formData.fertigationDurationMinutes);

    if (isNaN(durationMinutes) || durationMinutes < 1) return;

    if (validationError || fertigationDelayError || planOverlapError || !tankRecipeValidation.isValid) return;

    const updatedStep: IrrigationPlanStepFormData = {
      ...step,
      durationMinutes,
      fertigationStartDelayMinutes:
        (fertigationEnabled || hasTanks) && !isNaN(fertigationStartDelayMinutes)
          ? fertigationStartDelayMinutes
          : undefined,
      fertigationDurationMinutes:
        (fertigationEnabled || hasTanks) && !isNaN(fertigationDurationMinutes) ? fertigationDurationMinutes : undefined,
      mirrorTargetIds: selectedMirrorIds,
      tankEntries,
    };

    try {
      await onSave(updatedStep);
      onClose();
    } catch (error) {
      console.error("Erro ao salvar etapa:", error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!step) return null;

  return (
    <Modal title="Editar Setor" isOpen={isOpen} onClose={handleCancel} showCloseButton={true}>
      <div className="space-y-4">
        {/* Sector Name (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Setor</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">{step.sectorName}</div>
        </div>

        {/* Mirror Targets */}
        {mirrorTargetSectors !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Espelhamento de setores</label>
            <p className="text-xs text-gray-500 mb-2">Válvulas de setores que serão acionadas junto com este setor</p>

            {mirrorTargetSectors.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {mirrorTargetSectors.map((sector) => (
                  <label
                    key={sector.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm bg-gray-100 hover:bg-gray-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMirrorIds.includes(sector.id)}
                      onChange={() =>
                        setSelectedMirrorIds((prev) =>
                          prev.includes(sector.id) ? prev.filter((id) => id !== sector.id) : [...prev, sector.id]
                        )
                      }
                      className="h-3.5 w-3.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="text-gray-900">{sector.name}</span>
                  </label>
                ))}
              </div>
            )}

            {mirrorTargetSectors.length === 0 && !mirroredTargetInfo?.length && (
              <p className="text-xs text-gray-400 italic">Nenhum setor disponível para espelhamento</p>
            )}
          </div>
        )}

        {/* Other sectors' mirroring (read-only info) */}
        {mirroredTargetInfo && mirroredTargetInfo.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Espelhamento de outros setores</label>
            <div className="flex flex-wrap gap-2">
              {mirroredTargetInfo.map((info) => (
                <span
                  key={info.targetSectorId}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-gray-50 text-gray-500"
                >
                  {info.targetSectorName} (→ {info.sourceSectorName})
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Duração (minutos)</label>
          <input
            type="number"
            min="1"
            max="1440"
            value={formData.durationMinutes}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                durationMinutes: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          {planOverlapError && <p className="text-sm text-red-600 mt-2">{planOverlapError}</p>}
        </div>

        {/* Fertigation timing fields */}
        {fertigationEnabled && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Atraso da Fertirrigação (minutos)</label>
              <input
                type="number"
                min="0"
                max="1440"
                value={formData.fertigationStartDelayMinutes}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    fertigationStartDelayMinutes: e.target.value,
                  }))
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  fertigationDelayError ? "border-red-500" : "border-gray-300"
                }`}
              />
              {fertigationDelayError && <p className="text-sm text-red-500 mt-1">{fertigationDelayError}</p>}
            </div>

            {hasTanks ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duração da Fertirrigação (minutos)
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
                  {derivedDuration} min
                </div>
                <p className="text-xs text-gray-500 mt-1">Calculado automaticamente com base nos tanques</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duração da Fertirrigação (minutos)
                </label>
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={formData.fertigationDurationMinutes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      fertigationDurationMinutes: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        )}

        {/* Tanks Section — only when fertigation is enabled at the plan level */}
        {fertigationEnabled ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tanques</label>

            {/* Recipe validation error */}
            {tankRecipeValidation.errorMessage && (
              <p className="text-sm text-red-600 mb-2">{tankRecipeValidation.errorMessage}</p>
            )}

            {/* Tank rows */}
            {tankEntries.length === 0 && (
              <p className="text-xs text-gray-400 italic mb-2">
                {activeProjectTanks.length > 0
                  ? "Nenhum tanque configurado. Adicione tanques para programar a fertirrigação."
                  : "Nenhum tanque disponível. Atribua tanques ao projeto primeiro."}
              </p>
            )}

            {/* No active crop warning */}
            {tankEntries.length > 0 && !activeSectorCrop && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                <p className="text-xs text-amber-700">
                  ⚠ Este setor não tem cultura ativa. Cadastre a cultura e população de plantas para ver a aplicação em
                  g/planta.
                </p>
              </div>
            )}

            <div className="space-y-2">
              {tankEntries.map((entry) => {
                // Look up project tank for concentration/flow rate
                const pt = activeProjectTanks.find((p) => p.id === entry.projectTankId);
                const concentrationGpL = pt?.tank?.concentration_g_l ?? 0;
                const flowRateLh = pt?.flow_rate_lh ?? 0;
                const plantPopulation = activeSectorCrop?.plant_population ?? 0;

                // Clamp time to max available
                const clampedTime = Math.min(entry.timeMinutes, maxTankTime);

                // Grams per plant calculation
                const quantityGrams = (clampedTime / 60) * concentrationGpL * flowRateLh;
                const gramsPerPlant = plantPopulation > 0 ? quantityGrams / plantPopulation : 0;

                return (
                  <div key={entry.projectTankId} className="flex flex-col gap-2 p-2 bg-gray-50 rounded-lg">
                    {/* Tank select + nutrient + remove */}
                    <div className="flex items-center gap-2">
                      <select
                        value={entry.projectTankId}
                        onChange={(e) => {
                          const selected = [
                            ...activeProjectTanks,
                            ...tankEntries
                              .filter((t) => t.projectTankId !== entry.projectTankId)
                              .map((t) => activeProjectTanks.find((pt) => pt.id === t.projectTankId)!)
                              .filter(Boolean),
                          ].find((pt) => pt?.id === e.target.value);
                          if (selected) {
                            const idx = tankEntries.findIndex((t) => t.projectTankId === entry.projectTankId);
                            const updated = [...tankEntries];
                            updated[idx] = {
                              projectTankId: selected.id,
                              tankLabel: selected.tank.label,
                              nutrientName: selected.tank.nutrient.name,
                              timeMinutes: entry.timeMinutes,
                            };
                            setTankEntries(updated);
                          }
                        }}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        {[
                          ...activeProjectTanks.filter(
                            (pt) => pt.id === entry.projectTankId || !tankEntries.some((t) => t.projectTankId === pt.id)
                          ),
                        ].map((pt) => (
                          <option key={pt.id} value={pt.id}>
                            {pt.tank.label} — {pt.tank.nutrient.name}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-600 w-24 truncate">{entry.nutrientName}</span>
                      {tankRecipeValidation.fieldErrors[entry.projectTankId] && (
                        <span className="text-xs text-red-500">
                          {tankRecipeValidation.fieldErrors[entry.projectTankId]}
                        </span>
                      )}
                      <button
                        onClick={() => handleRemoveTank(entry.projectTankId)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Remover
                      </button>
                    </div>

                    {/* Time slider + display */}
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="1"
                        max={maxTankTime}
                        value={clampedTime}
                        onChange={(e) => handleTankChange(entry.projectTankId, "timeMinutes", parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                      />
                      <span className="text-sm font-medium text-gray-900 w-16 text-right">{clampedTime} min</span>
                      {plantPopulation > 0 ? (
                        <span className="text-xs text-gray-500">
                          {gramsPerPlant < 0.01
                            ? `${gramsPerPlant.toFixed(4)} g/planta`
                            : `${gramsPerPlant.toFixed(2)} g/planta`}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={handleAddTank}
              disabled={availableTanks.length === 0}
              className="mt-2 text-sm text-green-600 hover:text-green-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              + Adicionar tanque
            </button>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tanques</label>
            <p className="text-xs text-gray-500">
              Habilite a fertirrigação na configuração do plano para gerenciar tanques neste setor.
            </p>
          </div>
        )}

        {/* Validation Error */}
        {validationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{validationError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button onClick={handleCancel} variant="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !!validationError || !!fertigationDelayError || !!planOverlapError || !tankRecipeValidation.isValid
            }
            variant="primary"
          >
            Salvar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default StepEditDialog;
